import { socialWeightLbs, socialVolumeLbs } from "../../utils/socialDepth";
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { V, Haptic } from '../../utils/theme';
import { LS } from '../../utils/storage';
import { Icons } from '../../components/Icons';
import { Card, Btn, Field, Sheet, Chip, Stat, Progress, Skeleton, SkeletonCard, MsgBannerCtrl, SuccessToastCtrl, ConfirmCtrl } from '../../components/ui';
import { today, ago, fmtShort, fmtFull, uid, friendDisplayName, convW, wUnit, calc1RM, calcStreak } from '../../utils/helpers';
import { SocialAPI, SYNC_URL } from '../../utils/sync';
import { ShareCard } from '../../utils/share';
import { BADGE_DEFS, calcEarnedBadges } from '../../data/badges';
import { useLayout } from '../../utils/responsive';
import { IRON_RANKS } from '../../data/ranks';
import { HelpBtn, compressImage } from '../features';
import { IronScoreCard, RankBadge, DailyMissionsCard } from '../gamification';
import { useStreak, calcReadiness } from '../../components/dialogs';
import { SectionGrid } from '../hubs';
import { MS_PER_MINUTE, MS_PER_HOUR, MS_PER_DAY, ONLINE_THRESHOLD_MS } from '../../utils/constants';
import { apiPost } from '../../utils/api';
import { track } from '../../components/kit';

export function SocialCompare({s}){
  const { isDesktop } = useLayout();
  const getBest=(id)=>{let b=0;(s.workouts||[]).forEach(w=>w.exercises.forEach(e=>{if(e.exerciseId===id)e.sets.forEach(st=>{const wt=parseFloat(st.weight)||0;if(wt>b)b=wt;});}));return b;};
  const e1rmBest=(id)=>{let b=0;(s.workouts||[]).forEach(w=>w.exercises.forEach(e=>{if(e.exerciseId===id)e.sets.forEach(st=>{const e1rm=calc1RM(parseFloat(st.weight)||0,parseInt(st.reps)||0);if(e1rm>b)b=e1rm;});}));return b;};
  const weekW=(s.workouts||[]).filter(w=>w.date>=ago(7)).length;
  const bestWeek=Math.max(...Array.from({length:12},(_,i)=>(s.workouts||[]).filter(w=>{const d2=ago(i*7);const d3=ago((i+1)*7);return w.date<=d2&&w.date>d3;}).length),weekW);
  const streak=useStreak(s.workouts);
  const bestStreak=(()=>{let best=0,c=0;for(let i=0;i<365;i++){if((s.workouts||[]).some(w=>w.date===ago(i))){c++;if(c>best)best=c;}else c=0;}return best;})();
  const thisMonthW=(s.workouts||[]).filter(w=>w.date>=ago(30)).length;
  const bestMonth=Math.max(...Array.from({length:6},(_,i)=>(s.workouts||[]).filter(w=>w.date>=ago((i+1)*30)&&w.date<ago(i*30)).length),thisMonthW);
  const totalVol=Math.round((s.workouts||[]).filter(w=>w.date>=ago(7)).reduce((a,w)=>a+w.exercises.reduce((b,e)=>b+e.sets.reduce((c,st)=>c+(parseFloat(st.weight)||0)*(parseInt(st.reps)||0),0),0),0));
  const bestWeekVol=Math.max(...Array.from({length:8},(_,i)=>Math.round((s.workouts||[]).filter(w=>w.date>=ago((i+1)*7)&&w.date<ago(i*7)).reduce((a,w)=>a+w.exercises.reduce((b,e)=>b+e.sets.reduce((c,st)=>c+(parseFloat(st.weight)||0)*(parseInt(st.reps)||0),0),0),0))),totalVol);
  const bench=getBest("bench"),squat=getBest("squat"),dead=getBest("deadlift");
  const benchE1=e1rmBest("bench"),squatE1=e1rmBest("squat"),deadE1=e1rmBest("deadlift");
  const bestBench=Math.max(...(s.workouts||[]).map(w=>w.exercises.filter(e=>e.exerciseId==="bench").reduce((b,e)=>Math.max(b,...e.sets.map(st=>parseFloat(st.weight)||0)),0)),bench);
  const bestSquat=Math.max(...(s.workouts||[]).map(w=>w.exercises.filter(e=>e.exerciseId==="squat").reduce((b,e)=>Math.max(b,...e.sets.map(st=>parseFloat(st.weight)||0)),0)),squat);
  const bestDead=Math.max(...(s.workouts||[]).map(w=>w.exercises.filter(e=>e.exerciseId==="deadlift").reduce((b,e)=>Math.max(b,...e.sets.map(st=>parseFloat(st.weight)||0)),0)),dead);

  const pct=(cur,best)=>best>0?Math.min(100,Math.round(cur/best*100)):100;

  const Row=({label,current,best,unit,fmt})=>{
    const c=typeof fmt==="function"?fmt(current):current;
    const b=typeof fmt==="function"?fmt(best):best;
    const p=pct(current,best);
    const isNew=current>0&&current>=best&&best>0;
    return(
      <div style={{padding:"10px 0",borderBottom:`1px solid ${V.divider}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
          <span style={{fontSize:12,color:V.text3}}>{label}</span>
          <div style={{display:"flex",gap:12,alignItems:"center"}}>
            <span style={{fontSize:14,fontWeight:800,color:isNew?V.accent:V.text,fontFamily:V.mono}}>{c}{unit}</span>
            <span style={{fontSize:11,color:V.text3,fontFamily:V.mono}}>PB: {b}{unit}</span>
            {isNew&&<span style={{fontSize:11,color:V.accent,fontWeight:700,background:`${V.accent}15`,padding:"1px 5px",borderRadius:4}}>NEW PB</span>}
          </div>
        </div>
        <div style={{height:4,borderRadius:2,background:V.hoverBg}}>
          <div style={{height:"100%",borderRadius:2,background:isNew?V.accent:p>=80?V.accent:p>=50?V.warn:V.danger,
            width:`${p}%`,transition:"width .4s"}}/>
        </div>
        <div style={{fontSize:11,color:V.text3,marginTop:3,textAlign:"right"}}>{p}% of PB</div>
      </div>
    );
  };

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>You vs Your Best</div>

      <Card style={{padding:14}}>
        <div style={{fontSize:11,fontWeight:700,color:V.text3,marginBottom:10}}>CONSISTENCY</div>
        <Row label="This Week" current={weekW} best={bestWeek} unit=" workouts"/>
        <Row label="Streak" current={streak} best={bestStreak} unit=" days"/>
        <Row label="This Month" current={thisMonthW} best={bestMonth} unit=" workouts"/>
      </Card>

      <Card style={{padding:14}}>
        <div style={{fontSize:11,fontWeight:700,color:V.text3,marginBottom:10}}>VOLUME</div>
        <Row label="Weekly Volume" current={totalVol} best={bestWeekVol}
          fmt={v=>v>=1000?`${(v/1000).toFixed(1)}k`:v} unit=" lbs"/>
      </Card>

      <Card style={{padding:14}}>
        <div style={{fontSize:11,fontWeight:700,color:V.text3,marginBottom:10}}>BIG 3 — TOP WEIGHT</div>
        <Row label="Bench Press" current={bench} best={bestBench} unit={` ${wUnit(s.units)}`}/>
        <Row label="Squat" current={squat} best={bestSquat} unit={` ${wUnit(s.units)}`}/>
        <Row label="Deadlift" current={dead} best={bestDead} unit={` ${wUnit(s.units)}`}/>
      </Card>

      <Card style={{padding:14}}>
        <div style={{fontSize:11,fontWeight:700,color:V.text3,marginBottom:10}}>ESTIMATED 1RM</div>
        {[{l:"Bench",v:benchE1},{l:"Squat",v:squatE1},{l:"Deadlift",v:deadE1}].map(r=>(
          <div key={r.l} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",
            borderBottom:`1px solid ${V.divider}`}}>
            <span style={{fontSize:12,color:V.text3}}>{r.l}</span>
            <span style={{fontSize:14,fontWeight:800,color:V.purple,fontFamily:V.mono}}>{r.v>0?`${r.v} ${wUnit(s.units)}`:"—"}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ─── Social Sub-Page: Leaderboard ───
export function SocialLeaderboard({s,d}){
  const { isDesktop } = useLayout();
  const email=s.profile?.email;
  const [mode,setMode]=useState("group"); // group | friends | global
  const [metric,setMetric]=useState("streak"); // streak | volume | workouts | big3 | duels
  const [groups,setGroups]=useState(null);
  const [members,setMembers]=useState(null);
  const [selGroup,setSelGroup]=useState(null);
  const [friends,setFriends]=useState(null);
  const [loading,setLoading]=useState(false);
  const [globalData,setGlobalData]=useState(null);
  const [globalLoading,setGlobalLoading]=useState(false);

  useEffect(()=>{if(email)SocialAPI.getGroups(email).then(setGroups);},[email]);
  useEffect(()=>{if(email&&mode==="friends")SocialAPI.getFriends(email).then(r=>{if(r)setFriends(r);});},[email,mode]);
  useEffect(()=>{if(email&&mode==="global"){setGlobalLoading(true);SocialAPI.globalLeaderboard(email,metric).then(r=>{setGlobalData(r);setGlobalLoading(false);});}},[email,mode,metric]);

  const loadMembers=(code)=>{
    setSelGroup(code);setLoading(true);
    SocialAPI.getMembers(email,code).then(m=>{setMembers(m);setLoading(false);});
  };

  const streak=useStreak(s.workouts);
  const getBest=(id)=>{let b=0;(s.workouts||[]).forEach(w=>w.exercises.forEach(e=>{if(e.exerciseId===id)e.sets.forEach(st=>{const wt=parseFloat(st.weight)||0;if(wt>b)b=wt;});}));return socialWeightLbs(b,s.units);};
  const myBig3=getBest("bench")+getBest("squat")+getBest("deadlift");
  const myWeekVol=socialVolumeLbs((s.workouts||[]).filter(w=>w.date>=ago(7)).reduce((a,w)=>a+w.exercises.reduce((b2,e)=>b2+e.sets.reduce((c,st)=>c+(parseFloat(st.weight)||0)*(parseInt(st.reps)||0),0),0),0),s.units);
  const myWeekWorkouts=(s.workouts||[]).filter(w=>w.date>=ago(7)).length;
  const myDuelRecord=LS.get("ft-duel-record")||{wins:0,losses:0,ties:0};
  const myDuelWins=myDuelRecord.wins||0;

  const myRow={email,name:s.profile?.firstName?`${s.profile.firstName} ${s.profile.lastName||""}`.trim():"You",
    isMe:true,streak,big3:myBig3,weekVol:myWeekVol,weekWorkouts:myWeekWorkouts,duelWins:myDuelWins};

  const METRICS=[
    {id:"streak",label:"🔥 Streak",unit:"days",key:"streak"},
    {id:"workouts",label:"💪 Workouts",unit:"this week",key:"weekWorkouts"},
    {id:"volume",label:"⚡ Volume",unit:"lbs/week",key:"weekVol"},
    {id:"big3",label:"🏋️ Big 3",unit:"lbs",key:"big3"},
    {id:"duels",label:"⚔️ Duels",unit:"wins",key:"duelWins"},
  ];
  const curMetric=METRICS.find(m=>m.id===metric)||METRICS[0];

  const getMemberVal=(m)=>{
    if(metric==="streak")return parseInt(m.challenges?.find(c=>c.challenge_id==="streak")?.value)||0;
    if(metric==="big3")return parseInt(m.challenges?.find(c=>c.challenge_id==="big3")?.value)||0;
    if(metric==="volume")return parseInt(m.challenges?.find(c=>c.challenge_id==="weekVol")?.value)||0;
    if(metric==="workouts")return parseInt(m.challenges?.find(c=>c.challenge_id==="weekWorkouts")?.value)||0;
    if(metric==="duels")return parseInt(m.challenges?.find(c=>c.challenge_id==="duel_wins")?.value)||0;
    return 0;
  };

  const rankColor=(i)=>i===0?"#fbbf24":i===1?"#94a3b8":i===2?"#cd7f32":track();
  const rankLabel=(i)=>i===0?"🥇":i===1?"🥈":i===2?"🥉":`${i+1}`;

  const friendRows=useMemo(()=>{
    const rows=[myRow];
    (friends?.friends||[]).forEach(f=>{
      const fStreak=parseInt(f.challenges?.find?.(c=>c.challenge_id==="streak")?.value)||0;
      const fBig3=parseInt(f.challenges?.find?.(c=>c.challenge_id==="big3")?.value)||0;
      const fWeekVol=parseInt(f.challenges?.find?.(c=>c.challenge_id==="weekVol")?.value)||0;
      const fWeekWorkouts=parseInt(f.challenges?.find?.(c=>c.challenge_id==="weekWorkouts")?.value)||0;
      const fDuelWins=parseInt(f.challenges?.find?.(c=>c.challenge_id==="duel_wins")?.value)||0;
      rows.push({...f,name:friendDisplayName(f),isMe:false,
        streak:fStreak,big3:fBig3,weekVol:fWeekVol,weekWorkouts:fWeekWorkouts,duelWins:fDuelWins});
    });
    return rows.sort((a,b)=>{
      if(metric==="streak")return b.streak-a.streak;
      if(metric==="big3")return b.big3-a.big3;
      if(metric==="volume")return b.weekVol-a.weekVol;
      if(metric==="workouts")return b.weekWorkouts-a.weekWorkouts;
      if(metric==="duels")return b.duelWins-a.duelWins;
      return 0;
    });
  },[friends,metric,myRow.streak,myRow.big3,myRow.weekVol,myRow.weekWorkouts,myRow.duelWins]);

  const groupRows=useMemo(()=>{
    const rows=(members?.members||[]).map(m=>({...m,_val:getMemberVal(m)}));
    rows.sort((a,b)=>b._val-a._val);
    return rows;
  },[members,metric]);

  const maxVal=(rows,getVal)=>Math.max(...rows.map(getVal),1);

  const getMyMetricVal=()=>{
    if(metric==="streak")return myRow.streak;
    if(metric==="big3")return myRow.big3;
    if(metric==="volume")return myRow.weekVol;
    if(metric==="workouts")return myRow.weekWorkouts;
    if(metric==="duels")return myRow.duelWins;
    return 0;
  };

  const fmtVal=(val)=>{
    if(metric==="volume"&&val>=1000)return`${(val/1000).toFixed(1)}k`;
    return val;
  };

  const renderRow=(r,i,rows,getVal)=>{
    const val=getVal(r);
    const mv=maxVal(rows,getVal);
    const isMe=r.isMe||r.email===email;
    return(
      <Card key={i} style={{padding:10,
        border:isMe?`1.5px solid ${V.accent}40`:undefined,
        background:isMe?`${V.accent}06`:undefined}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{width:26,height:26,borderRadius:8,background:rankColor(i),
            display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:i<3?14:11,fontWeight:800,color:i<3?"#000":V.text3,flexShrink:0}}>
            {rankLabel(i)}
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
              <span style={{fontSize:12,fontWeight:isMe?700:500,color:isMe?V.accent:V.text,
                overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"60%"}}>
                {r.name||friendDisplayName(r)}{isMe?" (You)":""}
              </span>
              <span style={{fontSize:13,fontWeight:800,color:isMe?V.accent:V.text,fontFamily:V.mono}}>
                {fmtVal(val)} <span style={{fontSize:11,color:V.text3,fontWeight:400}}>{curMetric.unit}</span>
              </span>
            </div>
            <div style={{height:3,borderRadius:2,background:V.hoverBg}}>
              <div style={{height:"100%",borderRadius:2,
                background:isMe?V.accent:i===0?"#fbbf24":V.purple,
                width:`${Math.round(val/mv*100)}%`,transition:"width .4s"}}/>
            </div>
          </div>
          {metric==="duels"&&i===0&&val>0&&(
            <span style={{fontSize:14,marginLeft:2}}>👑</span>
          )}
        </div>
      </Card>
    );
  };

  return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{fontSize:16,fontWeight:800,color:V.text}}>Leaderboard</div>
        {metric==="duels"&&(
          <div style={{fontSize:11,padding:"3px 8px",borderRadius:6,
            background:`${V.accent}12`,border:`1px solid ${V.accent}25`,
            color:V.accent,fontWeight:700}}>DUEL KINGS</div>
        )}
      </div>

      {/* Mode toggle */}
      <div style={{display:"flex",gap:0,borderRadius:10,overflow:"hidden",border:`1px solid ${V.cardBorder}`}}>
        {[{id:"group",label:"My Group"},{id:"friends",label:"Friends"},{id:"global",label:"🌍 Global"}].map(m=>(
          <button key={m.id} onClick={()=>setMode(m.id)} style={{flex:1,padding:"9px 0",
            background:mode===m.id?V.accent:"transparent",border:"none",cursor:"pointer",
            fontSize:12,fontWeight:700,color:mode===m.id?V.bg:V.text3,fontFamily:V.font,transition:"all .2s"}}>
            {m.label}
          </button>
        ))}
      </div>

      {/* Metric picker */}
      <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
        {METRICS.map(m=>(
          <Chip key={m.id} label={m.label} active={metric===m.id} onClick={()=>setMetric(m.id)}/>
        ))}
      </div>

      {/* Duel wins context card */}
      {metric==="duels"&&(
        <Card style={{padding:12,background:`linear-gradient(135deg,rgba(0,245,160,0.06),rgba(139,92,246,0.06))`,
          border:`1px solid ${V.accent}20`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:V.text3,marginBottom:2}}>YOUR DUEL RECORD</div>
              <div style={{display:"flex",gap:12,alignItems:"center"}}>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:20,fontWeight:900,color:V.accent,fontFamily:V.mono}}>{myDuelRecord.wins||0}</div>
                  <div style={{fontSize:11,color:V.text3}}>W</div>
                </div>
                <div style={{fontSize:12,color:V.text3}}>—</div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:20,fontWeight:900,color:V.danger,fontFamily:V.mono}}>{myDuelRecord.losses||0}</div>
                  <div style={{fontSize:11,color:V.text3}}>L</div>
                </div>
                {(myDuelRecord.ties||0)>0&&(
                  <>
                    <div style={{fontSize:12,color:V.text3}}>—</div>
                    <div style={{textAlign:"center"}}>
                      <div style={{fontSize:20,fontWeight:900,color:V.warn,fontFamily:V.mono}}>{myDuelRecord.ties}</div>
                      <div style={{fontSize:11,color:V.text3}}>T</div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div style={{textAlign:"right"}}>
              {(myDuelRecord.wins+myDuelRecord.losses)>0&&(
                <div>
                  <div style={{fontSize:18,fontWeight:900,color:V.text,fontFamily:V.mono}}>
                    {Math.round((myDuelRecord.wins/(myDuelRecord.wins+myDuelRecord.losses))*100)}%
                  </div>
                  <div style={{fontSize:11,color:V.text3}}>win rate</div>
                </div>
              )}
              <button onClick={()=>d({type:"TAB",tab:"social_duels"})}
                style={{marginTop:6,padding:"4px 10px",borderRadius:7,background:V.accent,border:"none",
                  cursor:"pointer",fontSize:11,fontWeight:800,color:V.bg,fontFamily:V.font}}>
                Duel Now →
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* GROUP MODE */}
      {mode==="group"&&(
        groups?.groups?.length>0?(
          <div>
            <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
              {groups.groups.map(g=>(
                <button key={g.code} onClick={()=>loadMembers(g.code)} style={{padding:"6px 14px",borderRadius:8,
                  border:`1.5px solid ${selGroup===g.code?V.accent:V.cardBorder}`,
                  background:selGroup===g.code?`${V.accent}10`:"transparent",
                  cursor:"pointer",fontSize:11,fontWeight:700,
                  color:selGroup===g.code?V.accent:V.text3,fontFamily:V.font}}>
                  {g.name}
                </button>
              ))}
            </div>

            {!selGroup&&<Card style={{padding:20,textAlign:"center"}}>
              <div style={{fontSize:13,color:V.text3}}>Select a group to view leaderboard</div>
            </Card>}

            {loading&&selGroup&&<SkeletonCard lines={3}/>}

            {!loading&&groupRows.length>0&&(
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {groupRows.length>=2&&(
                  <Card style={{padding:14,marginBottom:4}}>
                    <div style={{display:"flex",justifyContent:"center",alignItems:"flex-end",gap:8,height:90}}>
                      {[1,0,2].filter(i=>i<groupRows.length).map(i=>{
                        const m=groupRows[i];
                        const val=m._val;
                        const h=i===0?70:i===1?55:45;
                        return(
                          <div key={i} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                            <span style={{fontSize:i===0?22:16}}>{rankLabel(i)}</span>
                            <div style={{fontSize:11,fontWeight:700,color:V.text,maxWidth:70,textAlign:"center",
                              overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{friendDisplayName(m)}</div>
                            <div style={{width:60,height:h,borderRadius:"6px 6px 0 0",
                              background:rankColor(i),display:"flex",alignItems:"flex-start",justifyContent:"center",paddingTop:4}}>
                              <span style={{fontSize:11,fontWeight:800,color:i<3?"#000":"#fff",fontFamily:V.mono}}>{fmtVal(val)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                )}
                {groupRows.map((m,i)=>renderRow(m,i,groupRows,r=>r._val))}
              </div>
            )}
          </div>
        ):(
          <Card style={{padding:30,textAlign:"center"}}>
            <div style={{fontSize:32,marginBottom:8}}>🏆</div>
            <div style={{fontSize:13,fontWeight:600,color:V.text,marginBottom:4}}>Join a group to compete</div>
            <div style={{fontSize:11,color:V.text3,marginBottom:14}}>Groups let you track rankings with your crew</div>
            <Btn full onClick={()=>d({type:"TAB",tab:"social_groups"})}>Find or Create Group</Btn>
          </Card>
        )
      )}

      {/* FRIENDS MODE */}
      {mode==="friends"&&(
        friendRows.length>1?(
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            <Card style={{padding:12,background:`linear-gradient(135deg,${V.accent}08,${V.purple}08)`}}>
              <div style={{fontSize:11,fontWeight:700,color:V.text3,marginBottom:6}}>YOUR RANKING</div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:24,fontWeight:900,color:V.accent,fontFamily:V.mono}}>
                    #{friendRows.findIndex(r=>r.isMe)+1}
                    <span style={{fontSize:12,color:V.text3,fontWeight:400}}> of {friendRows.length}</span>
                  </div>
                  <div style={{fontSize:11,color:V.text3}}>{curMetric.label}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:18,fontWeight:800,color:V.text,fontFamily:V.mono}}>{fmtVal(getMyMetricVal())}</div>
                  <div style={{fontSize:11,color:V.text3}}>{curMetric.unit}</div>
                </div>
              </div>
            </Card>
            {friendRows.map((r,i)=>renderRow(r,i,friendRows,row=>{
              if(metric==="streak")return row.streak;
              if(metric==="big3")return row.big3;
              if(metric==="volume")return row.weekVol;
              if(metric==="workouts")return row.weekWorkouts;
              if(metric==="duels")return row.duelWins;
              return 0;
            }))}
          </div>
        ):(
          <Card style={{padding:30,textAlign:"center"}}>
            <div style={{fontSize:32,marginBottom:8}}>👥</div>
            <div style={{fontSize:13,fontWeight:600,color:V.text,marginBottom:4}}>Add friends to compete</div>
            <div style={{fontSize:11,color:V.text3,marginBottom:14}}>Your friends' stats will appear here once added</div>
            <Btn full onClick={()=>d({type:"TAB",tab:"social_friends"})}>Add Friends</Btn>
          </Card>
        )
      )}

      {/* GLOBAL MODE */}
      {mode==="global"&&(
        globalLoading?<SkeletonCard lines={4}/>:
        globalData?.leaderboard?.length>0?(
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {globalData.myRank&&(
              <Card style={{padding:12,background:`linear-gradient(135deg,${V.accent}08,${V.purple}08)`}}>
                <div style={{fontSize:11,fontWeight:700,color:V.text3,marginBottom:6}}>YOUR GLOBAL RANKING</div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:24,fontWeight:900,color:V.accent,fontFamily:V.mono}}>
                      #{globalData.myRank}
                      <span style={{fontSize:12,color:V.text3,fontWeight:400}}> of {globalData.leaderboard.length}+</span>
                    </div>
                    <div style={{fontSize:11,color:V.text3}}>{curMetric.label}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:18,fontWeight:800,color:V.text,fontFamily:V.mono}}>{fmtVal(getMyMetricVal())}</div>
                    <div style={{fontSize:11,color:V.text3}}>{curMetric.unit}</div>
                  </div>
                </div>
              </Card>
            )}
            {/* Podium for top 3 */}
            {globalData.leaderboard.length>=2&&(
              <Card style={{padding:14,marginBottom:4}}>
                <div style={{display:"flex",justifyContent:"center",alignItems:"flex-end",gap:8,height:90}}>
                  {[1,0,2].filter(i=>i<globalData.leaderboard.length).map(i=>{
                    const entry=globalData.leaderboard[i];
                    const val=entry.value||0;
                    const h=i===0?70:i===1?55:45;
                    const name=entry.firstName||(entry.nickname?entry.nickname:(entry.username||"User"));
                    return(
                      <div key={i} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                        <span style={{fontSize:i===0?22:16}}>{rankLabel(i)}</span>
                        <div style={{fontSize:11,fontWeight:700,color:entry.isMe?V.accent:V.text,maxWidth:70,textAlign:"center",
                          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name}{entry.isMe?" (You)":""}</div>
                        <div style={{width:60,height:h,borderRadius:"6px 6px 0 0",
                          background:rankColor(i),display:"flex",alignItems:"flex-start",justifyContent:"center",paddingTop:4}}>
                          <span style={{fontSize:11,fontWeight:800,color:i<3?"#000":"#fff",fontFamily:V.mono}}>{fmtVal(val)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
            {globalData.leaderboard.map((entry,i)=>{
              const name=entry.firstName||(entry.nickname?entry.nickname:(entry.username||"User"));
              const mv=Math.max(...globalData.leaderboard.map(e=>e.value||0),1);
              return(
                <Card key={i} style={{padding:10,
                  border:entry.isMe?`1.5px solid ${V.accent}40`:undefined,
                  background:entry.isMe?`${V.accent}06`:undefined}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <div style={{width:26,height:26,borderRadius:8,background:rankColor(i),
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:i<3?14:11,fontWeight:800,color:i<3?"#000":V.text3,flexShrink:0}}>
                      {rankLabel(i)}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                        <span style={{fontSize:12,fontWeight:entry.isMe?700:500,color:entry.isMe?V.accent:V.text,
                          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"60%"}}>
                          {name}{entry.isMe?" (You)":""}
                        </span>
                        <span style={{fontSize:13,fontWeight:800,color:entry.isMe?V.accent:V.text,fontFamily:V.mono}}>
                          {fmtVal(entry.value||0)} <span style={{fontSize:11,color:V.text3,fontWeight:400}}>{curMetric.unit}</span>
                        </span>
                      </div>
                      <div style={{height:3,borderRadius:2,background:V.hoverBg}}>
                        <div style={{height:"100%",borderRadius:2,
                          background:entry.isMe?V.accent:i===0?"#fbbf24":V.purple,
                          width:`${Math.round((entry.value||0)/mv*100)}%`,transition:"width .4s"}}/>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ):(
          <Card style={{padding:30,textAlign:"center"}}>
            <div style={{fontSize:32,marginBottom:8}}>🌍</div>
            <div style={{fontSize:13,fontWeight:600,color:V.text,marginBottom:4}}>No global rankings yet</div>
            <div style={{fontSize:11,color:V.text3}}>Log workouts and sync to appear on the global leaderboard</div>
          </Card>
        )
      )}
    </div>
  );
}

// ─── Social Sub-Page: Challenges (kept from SocialHub) ───
export function SocialChallenges({s,d}){
  const { isDesktop } = useLayout();
  const streak=useStreak(s.workouts);
  const getBest=(id)=>{let b=0;(s.workouts||[]).forEach(w=>w.exercises.forEach(e=>{if(e.exerciseId===id)e.sets.forEach(st=>{if(st.weight>b)b=st.weight;});}));return b;};
  const big3=getBest("bench")+getBest("squat")+getBest("deadlift");
  const protDays=(s.nutrition||[]).filter(n=>n.date>=ago(7)&&(n.protein||0)>=(s.goals?.protein||180)).length;
  const tierOf=(val,tiers)=>{for(let i=tiers.length-1;i>=0;i--)if(val>=tiers[i].v)return tiers[i];return{name:"",icon:"",v:0};};
  const streakTiers=[{v:7,name:"Bronze",icon:"🥉"},{v:14,name:"Silver",icon:"🥈"},{v:21,name:"Gold",icon:"🥇"},{v:30,name:"Diamond",icon:"💎"}];
  const liftTiers=[{v:600,name:"Bronze",icon:"🥉"},{v:800,name:"Silver",icon:"🥈"},{v:1000,name:"Gold",icon:"🥇"},{v:1200,name:"Diamond",icon:"💎"}];
  const macroTiers=[{v:3,name:"Bronze",icon:"🥉"},{v:5,name:"Silver",icon:"🥈"},{v:7,name:"Gold",icon:"🥇"}];
  const weekNum=Math.floor((Date.now()-new Date("2026-01-01").getTime())/(7*86400000));
  const weeklyChallenges=[
    {name:"3 in 5",desc:"3 workouts in 5 days",check:()=>(s.workouts||[]).filter(w=>w.date>=ago(5)).length,total:3},
    {name:"Protein Week",desc:"Hit protein target 4 days",check:()=>protDays,total:4},
    {name:"Volume King",desc:"5,000 lbs total squat volume this week",check:()=>{let v=0;(s.workouts||[]).filter(w=>w.date>=ago(7)).forEach(w=>w.exercises.forEach(e=>{if(e.exerciseId==="squat")e.sets.forEach(st=>{v+=(parseFloat(st.weight)||0)*(parseInt(st.reps)||0);});}));return Math.round(v);},total:5000},
    {name:"Full Logger",desc:"Log workout + meal + check-in today",check:()=>((s.workouts||[]).some(w=>w.date===today())?1:0)+((s.nutrition||[]).some(n=>n.date===today())?1:0)+((s.checkins||[]).some(c=>c.date===today())?1:0),total:3},
  ];
  const weeklyChallenge=weeklyChallenges[weekNum%weeklyChallenges.length];

  return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{display:"flex",alignItems:"center",gap:6}}><span style={{fontSize:16,fontWeight:800,color:V.text}}>Challenges</span><HelpBtn topic="challenges" color="#a78bfa"/></div>
      {[
        {name:"Workout Streak",desc:"Consecutive days with a logged workout",emoji:"🔥",val:streak,tiers:streakTiers,nextTier:streakTiers.find(t=>t.v>streak),unit:" days"},
        {name:"Big 3 Total",desc:"Best Bench + Squat + Deadlift",emoji:"🏋️",val:big3,tiers:liftTiers,nextTier:liftTiers.find(t=>t.v>big3),unit:` ${wUnit(s.units)}`},
        {name:"Macro Master",desc:"Days hitting protein in last 7",emoji:"🥗",val:protDays,tiers:macroTiers,nextTier:macroTiers.find(t=>t.v>protDays),unit:"/7 days"},
      ].map((ch,i)=>{
        const tier=tierOf(ch.val,ch.tiers);
        return(
          <Card key={i} style={{padding:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
              <div><div style={{fontSize:14,fontWeight:700,color:V.text}}>{ch.emoji} {ch.name}</div>
                <div style={{fontSize:11,color:V.text3}}>{ch.desc}</div></div>
              {tier.name&&<div style={{display:"flex",gap:4,alignItems:"center"}}>
                <span style={{padding:"3px 8px",borderRadius:6,fontSize:11,fontWeight:700,background:`${V.accent}10`,color:V.accent}}>{tier.icon} {tier.name}</span>
              </div>}
            </div>
            <div style={{fontSize:20,fontWeight:800,color:V.text,fontFamily:V.mono,margin:"6px 0"}}>{ch.val}{ch.unit}</div>
            {ch.nextTier&&(<div>
              <div style={{height:4,borderRadius:2,background:V.hoverBg,marginBottom:4}}>
                <div style={{height:"100%",borderRadius:2,background:V.accent,width:`${Math.min(100,(ch.val/ch.nextTier.v)*100)}%`,transition:"width .3s"}}/>
              </div>
              <div style={{fontSize:11,color:V.text3}}>{ch.nextTier.v-ch.val}{ch.unit} to {ch.nextTier.icon} {ch.nextTier.name}</div>
            </div>)}
            {!ch.nextTier&&tier.name&&<span style={{fontSize:11,color:V.accent,fontWeight:600}}>Max tier reached! 🎉</span>}
          </Card>
        );
      })}
      <Card style={{padding:14,border:`1px solid rgba(236,72,153,0.2)`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
          <span style={{fontSize:13,fontWeight:700,color:"#ec4899"}}>🔄 Weekly Challenge</span>
          <span style={{fontSize:11,color:V.text3}}>Resets Monday</span>
        </div>
        <div style={{fontSize:14,fontWeight:700,color:V.text}}>{weeklyChallenge.name}</div>
        <div style={{fontSize:11,color:V.text3,marginBottom:6}}>{weeklyChallenge.desc}</div>
        <div style={{fontSize:18,fontWeight:800,color:weeklyChallenge.check()>=weeklyChallenge.total?"#ec4899":V.text,fontFamily:V.mono}}>
          {weeklyChallenge.check()} / {weeklyChallenge.total} {weeklyChallenge.check()>=weeklyChallenge.total?"✅":""}
        </div>
        <div style={{height:4,borderRadius:2,background:V.hoverBg,marginTop:4}}>
          <div style={{height:"100%",borderRadius:2,background:"#ec4899",width:`${Math.min(100,weeklyChallenge.check()/weeklyChallenge.total*100)}%`}}/>
        </div>
      </Card>
    </div>
  );
}



// ═══════════════════════════════════════════════════════
// IRONSCORE — XP + Level system (runs fully client-side)
// ═══════════════════════════════════════════════════════
// IRON_RANKS imported from ../data/ranks

// ── XP Bonus Pool (missions, multipliers, one-time events) ──
// Stored in LS as {total, log:[{date,amount,reason}]}
